<template>
  <div>
    <div class="modal-body">
      <form @submit.prevent="updateVideo">
        <!-- STEP INDICATOR -->
        <div class="mb-4 text-center">
          <span :class="['step-badge', step === 1 ? 'active' : '']">1</span>
          <span class="mx-2">—</span>
          <span :class="['step-badge', step === 2 ? 'active' : '']">2</span>
        </div>

        <!-- STEP 1 -->
        <div v-if="step === 1">
          <div class="mb-3">
            <label class="form-label">Video Title</label>
            <input
              type="text"
              class="form-control"
              v-model="form.title"
              required
            />
          </div>
          <div class="mb-3">
            <label class="form-label">YouTube Link</label>
            <input
              type="url"
              class="form-control"
              v-model="form.link"
              required
            />
          </div>
          <div class="mb-3">
            <label class="form-label">Description</label>
            <textarea
              class="form-control"
              rows="4"
              v-model="form.description"
              required
            ></textarea>
          </div>
        </div>

        <!-- STEP 2 -->
        <div v-if="step === 2">
          <div class="mb-3">
            <label class="form-label">Grade</label>
            <select class="form-select" v-model="form.grade_id" required>
              <option value="" disabled>Select Grade</option>
              <option v-for="g in grades" :key="g.id" :value="g.id">
                {{ g.level }}
              </option>
            </select>
          </div>

          <div class="mb-3">
            <label class="form-label">Subject</label>
            <select
              class="form-select"
              v-model="form.subject_id"
              :disabled="!form.grade_id"
              required
            >
              <option value="" disabled>Select Subject</option>
              <option v-for="s in subjects" :key="s.id" :value="s.id">
                {{ s.name }}
              </option>
            </select>
          </div>

          <div class="mb-3">
            <label class="form-label">Topic</label>
            <select
              class="form-select"
              v-model="form.topic_id"
              :disabled="!form.subject_id"
              required
            >
              <option value="" disabled>Select Topic</option>
              <option v-for="t in topics" :key="t.id" :value="t.id">
                {{ t.title }}
              </option>
            </select>
          </div>

          <div class="mb-3">
            <label class="form-label">Month</label>
            <select class="form-select" v-model="form.month" required>
              <option value="" disabled>Select Month</option>
              <option v-for="m in months" :key="m.value" :value="m.value">
                {{ m.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- FOOTER -->
        <div class="modal-footer p-0 mt-3">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
          >
            Close
          </button>

          <button
            v-if="step === 2"
            type="button"
            class="btn btn-outline-secondary"
            @click="step--"
          >
            Back
          </button>

          <button
            v-if="step === 1"
            type="button"
            class="btn btn-primary"
            @click="step = 2"
          >
            Next
          </button>

          <button
            v-if="step === 2"
            type="submit"
            class="btn btn-pink"
            :disabled="loading"
          >
            <span
              v-if="loading"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            {{ loading ? "Updating..." : "Update" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "EditVideo",
  props: { video: { type: Object, required: true } },
  data() {
    return {
      step: 1,
      loading: false,
      grades: [],
      subjects: [],
      topics: [],
      form: {
        title: "",
        description: "",
        link: "",
        grade_id: "",
        subject_id: "",
        topic_id: "",
        month: "",
        status: "active",
      },
      months: [
        { value: 1, label: "January" },
        { value: 2, label: "February" },
        { value: 3, label: "March" },
        { value: 4, label: "April" },
        { value: 5, label: "May" },
        { value: 6, label: "June" },
        { value: 7, label: "July" },
        { value: 8, label: "August" },
        { value: 9, label: "September" },
        { value: 10, label: "October" },
        { value: 11, label: "November" },
        { value: 12, label: "December" },
      ],
    };
  },

  watch: {
    video: {
      immediate: true,
      handler: async function (newVal) {
        if (!newVal) return;

        this.form.title = newVal.title || "";
        this.form.description = newVal.description || "";
        this.form.link = newVal.link || "";
        this.form.month = newVal.month || "";
        this.form.status = newVal.status || "active";

        this.form.grade_id = newVal.grade_id || "";

        if (this.form.grade_id) {
          await this.fetchSubjectsByGrade();
          this.form.subject_id = newVal.subject_id || "";
        }

        if (this.form.subject_id) {
          await this.fetchTopicsBySubject();
          this.form.topic_id = newVal.topic_id || "";
        }
      },
    },

    "form.grade_id"(newVal) {
      // Reset child dropdowns
      this.form.subject_id = "";
      this.form.topic_id = "";
      this.subjects = [];
      this.topics = [];

      if (newVal) this.fetchSubjectsByGrade();
    },

    "form.subject_id"(newVal) {
      this.form.topic_id = "";
      this.topics = [];

      if (newVal) this.fetchTopicsBySubject();
    },
  },

  mounted() {
    this.fetchGrades();
  },

  methods: {
    async fetchGrades() {
      try {
        const res = await axios.get("/a-get-grades");
        this.grades = res.data;
      } catch (err) {
        console.error(err);
      }
    },

    async fetchSubjectsByGrade() {
      if (!this.form.grade_id) return;

      try {
        const res = await axios.get(
          `/a-grade-subjects?grade_id=${this.form.grade_id}`,
        );
        // Adjust depending on API response
        this.subjects = res.data.subjects || res.data;
      } catch (err) {
        console.error(err);
      }
    },

    async fetchTopicsBySubject() {
      if (!this.form.subject_id) return;

      try {
        const res = await axios.get(
          `/a-get-subject-topics?subject_id=${this.form.subject_id}`,
        );
        this.topics = res.data.topics || res.data;
      } catch (err) {
        console.error(err);
      }
    },

    async updateVideo() {
      this.loading = true;
      try {
        const res = await axios.put(
          `/update-video/${this.video.id}`,
          this.form,
        );

        this.$emit("video-edited", {
          type: "success",
          message: res.data.message || "Video updated successfully!",
        });
      } catch (err) {
        this.$emit("video-edited", {
          type: "error",
          message: err.response?.data?.message || "Failed to update video!",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.btn-pink {
  background-color: #e83e8c;
  color: #fff;
  border: none;
}
.btn-pink:hover {
  background-color: #d63384;
  color: #fff;
}
</style>
